import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import "../styles/starthere.css";

const STEPS = [
  {
    n: "1",
    title: "Download the app",
    body: "Install Ego Desktop. It turns your computer into a node on the network.",
  },
  {
    n: "2",
    title: "Share what you are not using",
    body: "Rent out the storage and graphics power your machine is not using.",
  },
  {
    n: "3",
    title: "Get paid when it is used",
    body: "When someone actually uses your space or power, you earn.",
  },
];

const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

const LINE_SECS = 1.4;

function Track() {
  const still = useReducedMotion();
  const view = { once: true, amount: 0.6 };
  return (
    <div className="sh-track" aria-hidden="true">
      <div className="sh-line">
        <motion.div
          className="sh-line-fill"
          initial={{ scaleX: still ? 1 : 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={view}
          transition={{ duration: still ? 0 : LINE_SECS, ease: "easeInOut" }}
        />
        {!still && (
          <motion.span
            className="sh-packet"
            initial={{ left: "0%", opacity: 0 }}
            whileInView={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }}
            viewport={{ once: false, amount: 0.6 }}
            transition={{
              delay: LINE_SECS + 0.4,
              duration: 2.2,
              times: [0, 0.1, 0.9, 1],
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 0.8,
            }}
          />
        )}
      </div>
      {STEPS.map((s, i) => (
        <div className="sh-node-slot" key={s.n}>
          <motion.span
            className="sh-node"
            initial={still ? false : { scale: 0.4, opacity: 0.25 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={view}
            transition={{
              delay: still ? 0 : (LINE_SECS * i) / (STEPS.length - 1),
              type: "spring",
              stiffness: 320,
              damping: 14,
            }}
          >
            {s.n}
            {!still && (
              <motion.span
                className="sh-ring"
                initial={{ scale: 1, opacity: 0 }}
                whileInView={{ scale: [1, 1.9], opacity: [0.55, 0] }}
                viewport={view}
                transition={{
                  delay: (LINE_SECS * i) / (STEPS.length - 1) + 0.15,
                  duration: 1.6,
                  repeat: Infinity,
                  repeatDelay: 1.4,
                  ease: "easeOut",
                }}
              />
            )}
          </motion.span>
        </div>
      ))}
    </div>
  );
}

export default function StartHere() {
  return (
    <section className="sh-band" id="start-here">
      <div className="sh-inner">
        <motion.div
          className="sh-head"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <span className="sh-tag">New to crypto? Start here</span>
          <h2 className="sh-title">
            Have you ever thought about mining crypto, or wondered how you would even start?
          </h2>
          <p className="sh-lede">
            We built a blockchain that anyone can help run, even with no coding or crypto
            experience.
          </p>
        </motion.div>

        <Track />

        <ol className="sh-steps">
          {STEPS.map((s, i) => (
            <motion.li
              key={s.n}
              className="sh-step"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: (LINE_SECS * i) / (STEPS.length - 1) }}
            >
              <span className="sh-num" aria-hidden="true">{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </motion.li>
          ))}
        </ol>

        <motion.div
          className="sh-body"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <p>
            You do not need special hardware. You do not need to understand how any of it
            works. You install it, leave it running, and your computer earns from space and
            power it was wasting anyway.
          </p>
          <p className="sh-fees">
            The fees people pay to use the network go to the people running it, not to us.
          </p>
        </motion.div>

        <div className="sh-actions">
          <Link to="/download" className="sh-btn primary" onClick={top}>
            Download Ego Desktop
          </Link>
          <Link to="/blog" className="sh-btn" onClick={top}>
            Read how it works
          </Link>
        </div>
      </div>
    </section>
  );
}
